import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import {
  AccountMethods,
  AccountJwt,
  AnyAccountJwtVO,
  Conf2AccountJwtVO,
  GuestAccountJwtVO,
  IAMAccountJwtVO,
  IdentityAccountJwtVO,
  KeycloakAccountJwtVO,
  AnyAccountJwtVOSpecs
} from "@common/domain";
import { jwtDecode } from "jwt-decode";
import { CookieOptions, CookieService } from "ngx-cookie";
import {
  BehaviorSubject,
  EMPTY,
  fromEvent,
  interval,
  Observable,
  Subject
} from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  retry,
  takeUntil
} from "rxjs/operators";
import { ACCOUNT_JWT_CONFIG, AccountJwtConfig } from "../providers";

// Why this page deleted its token (logs)
export type AccountJwtDeleteReason =
  | "sign-out"
  | "conf2-sign-in"
  | "removed-from-room";

export type AccountJwtEvent =
  // The sessionStorage token vanished although no page deleted it: written back
  | { type: "restored" }
  // Not written back: expired, or the storage refused the write
  | {
      type: "restore-declined";
      reason: "expired" | "invalid" | "storage-error";
    }
  // Deleted on purpose by this page, or by another frame of the tab
  | { type: "deleted"; reason: AccountJwtDeleteReason | "other-frame" };

@Injectable()
export class AccountJwtService implements OnDestroy {
  private static ACCOUNT_JWT_COOKIE_PATH = "/";

  private readonly _accountJwt$: BehaviorSubject<null | AccountJwt>;
  private readonly destroy$ = new Subject<void>();
  private readonly sessionStorage: globalThis.Storage;
  // The token this page keeps in its tab's sessionStorage (Conf2, guest: an
  // iframe has no cookie). Null once the page deleted it itself (sign-out,
  // new sign-in) and for cookie tokens.
  private sessionJwt: null | AccountJwt = null;
  private readonly _accountJwtEvents$ = new Subject<AccountJwtEvent>();
  // The storage refused the write-back (said once per loss)
  private restoreRefused = false;
  // The token this page deleted on purpose: never adopted again from the
  // storage, even if the removal failed (storage full, access revoked)
  private deletedJwt: null | AccountJwt = null;

  anyAccountJwtVO$: Observable<null | AnyAccountJwtVO>;
  identityAccountJwtVO$: Observable<null | IdentityAccountJwtVO>;

  private static tokenState(jwt: AccountJwt): "valid" | "expired" | "invalid" {
    try {
      const { exp } = jwtDecode<{ exp?: number }>(jwt);
      return typeof exp === "number" && exp * 1000 <= Date.now()
        ? "expired"
        : "valid";
    } catch {
      // Not decodable: not a token to keep
      return "invalid";
    }
  }

  // Written to the shared sessionStorage by a page that deletes the token on
  // purpose, before the removal: another frame of the tab polling before the
  // 'storage' event arrives must not write the token back
  private get deletedMarker() {
    return `${this.accountJwtConfig.name}:deleted`;
  }

  constructor(
    @Inject(ACCOUNT_JWT_CONFIG)
    private readonly accountJwtConfig: AccountJwtConfig,
    private readonly cookieService: CookieService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.sessionStorage = this.document.defaultView!.sessionStorage;

    const initial = this.accountJwtRead();
    this.sessionJwt = initial.fromSessionStorage ? initial.jwt : null;
    this._accountJwt$ = new BehaviorSubject<null | AccountJwt>(initial.jwt);

    this.accountJwtObserve$().pipe(takeUntil(this.destroy$)).subscribe();
    this.otherFrameObserve$().pipe(takeUntil(this.destroy$)).subscribe();

    this.anyAccountJwtVO$ = this._accountJwt$.pipe(
      distinctUntilChanged(),
      map((_) => (_ ? this.anyAccountJwtCreate(_) : null))
    );
    this.identityAccountJwtVO$ = this.anyAccountJwtVO$.pipe(
      map((_) =>
        !!_ && AnyAccountJwtVOSpecs.isIdentityAccountJwtVO(_)
          ? (_ as IdentityAccountJwtVO)
          : null
      )
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this._accountJwt$.complete();
    this._accountJwtEvents$.complete();
  }

  get accountJwt(): null | AccountJwt {
    return this._accountJwt$.value;
  }

  get accountJwt$(): Observable<null | AccountJwt> {
    return this._accountJwt$.pipe(distinctUntilChanged());
  }

  /** Restores, declined restores and deletions of the token, for the logs. */
  get accountJwtEvents$(): Observable<AccountJwtEvent> {
    return this._accountJwtEvents$.asObservable();
  }

  accountJwtDelete(reason: AccountJwtDeleteReason) {
    const { name, domain } = this.accountJwtConfig;

    // Explicit: never restored, here nor by another frame of the tab
    this.deletedJwt = this._accountJwt$.value;
    this.sessionJwt = null;
    this.restoreRefused = false;
    this._accountJwtEvents$.next({ type: "deleted", reason });

    // Separately: a failed marker write must not skip the removal
    try {
      this.sessionStorage.setItem(this.deletedMarker, "1");
    } catch {
      // Without the marker, only the 'storage' event tells the other frames
    }
    try {
      this.sessionStorage.removeItem(name);
    } catch {
      // Left in the storage: deletedJwt keeps this page from adopting it again
    }
    this.cookieService.remove(name, {
      domain,
      path: AccountJwtService.ACCOUNT_JWT_COOKIE_PATH
    });

    this._accountJwt$.next(null);
  }

  accountJwtSet(jwt: AccountJwt, sessionStorage = false) {
    const { name, domain } = this.accountJwtConfig;

    if (sessionStorage) {
      this.deletedJwt = null;
      this.sessionStorage.removeItem(this.deletedMarker);
      this.sessionStorage.setItem(name, jwt);
      this.sessionJwt = jwt;
      this.restoreRefused = false;
      this._accountJwt$.next(jwt);

      return;
    }

    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    const options: CookieOptions = {
      expires: oneYearFromNow,
      path: AccountJwtService.ACCOUNT_JWT_COOKIE_PATH,
      domain
    };

    this.cookieService.put(name, jwt, options);
    this.deletedJwt = null;
    this.sessionJwt = null;
    this._accountJwt$.next(jwt);
  }

  private accountJwtRead(): {
    jwt: null | AccountJwt;
    fromSessionStorage: boolean;
  } {
    const { name } = this.accountJwtConfig;

    const fromSession = this.sessionStorage.getItem(name);
    if (fromSession) {
      return { jwt: fromSession as AccountJwt, fromSessionStorage: true };
    }
    const fromCookie = this.cookieService.get(name);
    return {
      jwt: fromCookie ? (fromCookie as AccountJwt) : null,
      fromSessionStorage: false
    };
  }

  private accountJwtGet(): null | AccountJwt {
    const { jwt, fromSessionStorage } = this.accountJwtRead();
    // The token this page deleted, still there because its removal failed
    if (jwt !== null && jwt === this.deletedJwt) {
      return null;
    }
    if (jwt !== null) {
      // Written by another frame of the tab (new sign-in): the one to keep
      if (fromSessionStorage) {
        this.sessionJwt = jwt;
        this.restoreRefused = false;
      }
      return jwt;
    }
    if (this.sessionJwt === null) {
      return null;
    }
    // Deleted on purpose by another frame of the tab (its sign-out)
    if (this.sessionStorage.getItem(this.deletedMarker) !== null) {
      this.sessionJwt = null;
      this._accountJwtEvents$.next({ type: "deleted", reason: "other-frame" });
      return null;
    }

    // The sessionStorage token is gone but no page deleted it (2026-11-03,
    // session 839721: a guest's page lost it, left the room and played its own
    // microphone back). Write it back: the page held it in memory anyway, and
    // the server keeps checking it on every request and socket packet (its
    // signature and expiry; a 1401 there signs this page out explicitly).
    // Expired: let it go. A cookie token (identity account) is never restored:
    // its removal is a sign-out in another tab.
    const state = AccountJwtService.tokenState(this.sessionJwt);
    if (state !== "valid") {
      this.sessionJwt = null;
      this._accountJwtEvents$.next({ type: "restore-declined", reason: state });
      return null;
    }
    try {
      this.sessionStorage.setItem(this.accountJwtConfig.name, this.sessionJwt);
      this.restoreRefused = false;
      this._accountJwtEvents$.next({ type: "restored" });
    } catch {
      // The storage refused it: keep the token in memory (the poll must not
      // die), say it once per loss
      if (!this.restoreRefused) {
        this.restoreRefused = true;
        this._accountJwtEvents$.next({
          type: "restore-declined",
          reason: "storage-error"
        });
      }
    }
    return this.sessionJwt;
  }

  // A removal by another same-origin frame of the tab (they share the
  // sessionStorage) is a deliberate one, e.g. its sign-out: not restored
  private otherFrameObserve$(): Observable<void> {
    const window = this.document.defaultView;
    if (!window) {
      return EMPTY;
    }
    return fromEvent<StorageEvent>(window, "storage").pipe(
      filter(
        (_) =>
          _.storageArea === this.sessionStorage &&
          (_.key === null ||
            (_.key === this.accountJwtConfig.name && _.newValue === null))
      ),
      map(() => {
        if (this.sessionJwt !== null) {
          this.sessionJwt = null;
          this._accountJwtEvents$.next({
            type: "deleted",
            reason: "other-frame"
          });
        }
      })
    );
  }

  private accountJwtObserve$(): Observable<void> {
    return interval(500).pipe(
      map(() => this.accountJwtGet()),
      distinctUntilChanged(),
      map((_) => this._accountJwt$.next(_)),
      // A storage read that throws (access revoked...) must not stop the poll
      retry({ delay: 500 })
    );
  }

  private anyAccountJwtCreate(anyAccountJwt: AccountJwt): AnyAccountJwtVO {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = jwtDecode<any>(anyAccountJwt);

    switch (payload["method"]) {
      case AccountMethods.Guest:
        return new GuestAccountJwtVO(payload);
      case AccountMethods.IAM:
        return new IAMAccountJwtVO(payload);
      case AccountMethods.Keycloak:
        return new KeycloakAccountJwtVO(payload);
      case AccountMethods.Conf2:
        return new Conf2AccountJwtVO(payload);
      default:
        throw new Error("Invalid token method");
    }
  }
}
